/**
 * Entrypoint — QueryClient + AuthProvider + RouterProvider stack.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { AuthProvider } from "@/auth/AuthContext";
import { fetchChatTopics } from "@/api/dashboard";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { startVersionWatch } from "@/lib/versionWatch";
import { PeriodProvider } from "@/state/period";
import { buildRouter } from "@/router";
import { bootstrapTheme } from "@/lib/useTheme";
import { setBrandFavicon } from "@/lib/setBrandFavicon";
import "@/styles/global.css";
import "@/styles/chat.css";
// TG-стиль (тот же файл, что грузится на production-сабдомене).
// Импортируется ПОСЛЕ global.css, чтобы каскад перебивал базовые brick-правила.
import "@/styles/production-tg.css";

// Применяем сохранённую тему ДО рендера React — иначе flash светлой
bootstrapTheme();

// Заголовок вкладки по сабдомену — dist-sales обслуживает несколько доменов.
(() => {
  const h = typeof window !== "undefined" ? window.location.hostname : "";
  if (h.startsWith("finance.")) document.title = "Кирпичные беседки · Финансы";
  else if (h.startsWith("sales.")) document.title = "Кирпичные беседки · Отдел продаж";
  else if (/(^|\.)brixchat\.ru$|^chat\.brixcrm\.ru$/.test(h)) { document.title = "BrixChat"; document.documentElement.dataset.brand = "brixchat"; }
})();

// Динамический favicon — узнаваемый цвет/буквы под сабдомен.
// Логика и палитра в src/lib/setBrandFavicon.ts (импортируется во всех
// четырёх main.*.tsx, чтобы каждый bundle сам поднял свой fav).
setBrandFavicon("auto");

// На messenger-build (VITE_APP_MODE=chat) ставим body[data-app-mode="chat"]
// ГЛОБАЛЬНО на boot — до рендера React. Это активирует все CSS-правила в
// global.css/chat-redesign.css/messenger-mobile.css которые префиксованы
// этим атрибутом (login-shell зелёный, composer, поиск и т.д.). До этого
// атрибут ставился только в MessengerPage useEffect → на /login стили
// были мёртвые.
if ((import.meta.env.VITE_APP_MODE as string | undefined) === "chat") {
  document.body.setAttribute("data-app-mode", "chat");
}

// Sales-бандл обслуживает sales-дашборд (главный домен + sales.<домен>).
// Помечаем body[data-bundle="sales"] чтобы production-tg.css мог сузить
// сайдбар именно для sales (production-сабдомен загружает тот же CSS,
// но с другим body-атрибутом — там сайдбар остаётся стандартной ширины).
if (typeof window !== "undefined" && (import.meta.env.VITE_APP_MODE as string | undefined) !== "chat") {
  const h = window.location.hostname;
  const isSubdomainBundle = /^(production|finance|warehouse|messenger)\./i.test(h);
  if (!isSubdomainBundle) {
    document.body.setAttribute("data-bundle", "sales");
  }
}

// PWA Service Worker — авто-обновление без ручного вмешательства:
// 1. immediate: сразу регистрирует SW при старте
// 2. onNeedRefresh: новый SW активируется → форсируем reload (без вопроса)
// 3. onRegisteredSW: каждые 60 сек проверяет наличие новой версии
// Это убирает проблему «PWA залип на старом бандле» — никаких ручных
// «Сбросить кэш / переустановить» больше не нужно.
import { registerSW } from "virtual:pwa-register";
const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    void updateSW(true);
  },
  onRegisteredSW(_swUrl, registration) {
    if (!registration) return;
    setInterval(() => {
      registration.update().catch(() => { /* offline — пропускаем */ });
    }, 60 * 1000);
  },
});

// Сторож версии (18.09.2026): onNeedRefresh выше не срабатывает — sw.js между
// сборками одинаков. Только мессенджер: на дашбордах скрытая вкладка может
// держать несохранённую форму.
if (typeof window !== "undefined"
    && ((import.meta.env.VITE_APP_MODE as string | undefined) === "chat" || window.location.pathname.includes("/messenger"))) {
  startVersionWatch();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Cold-start: на messenger-build стартуем загрузку списка чатов СРАЗУ (кука
// сессии уже есть), параллельно с /users/me — waterfall был
// /users/me → mount ChatPage → chat/topics. Теперь к mount'у топики уже в кэше,
// минус ~1 RTT до первого контента. На /login запрос вернёт 401 —
// prefetchQuery проглатывает без ошибки в консоли.
if ((import.meta.env.VITE_APP_MODE as string | undefined) === "chat") {
  void queryClient.prefetchQuery({ queryKey: ["chat/topics"], queryFn: fetchChatTopics });
}

const router = buildRouter(queryClient);

// Светлая тема — 1:1 с прототипом untitled/project (handoff Claude Design)
// (если нужно тёмную — раскомментируй: document.documentElement.dataset.theme = "dark";)

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("No #root element");

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PeriodProvider>
            <RouterProvider router={router} />
          </PeriodProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
